import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";
import { Formik, Form } from "formik";

import useNotification from "src/hooks/notification";
import { getScanFields, findItemsByMobileLocation } from "src/utils/assetUtils";
import AssetImage from "src/components/wizard/Item/AssetImage";
import ScannerInput from "src/components/form/ScannerInput";
import useMutation from "src/hooks/useMutation";
import {
  UPDATE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_REQUEST_MUTATION,
} from "src/constants/graphql/mutations";

const AUTO_SUBMIT_MIN_LENGTH = 3;
const AUTO_SUBMIT_IDLE_MS = 120;
const BURST_KEY_GAP_MS = 50;

export default function MasterScanOrEnterModal({
  inventoryRequest,
  inventoryItems,
  onClose,
  onScanComplete,
}) {
  const [loading, setLoading] = useState(false);
  const [mobileLocationItems, setMobileLocationItems] = useState(null);
  const [pickedItems, setPickedItems] = useState([]);
  const { notifyError, notifySuccess } = useNotification();
  const [updateInventoryItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const formikRef = useRef(null);
  const searchInputRef = useRef(null);
  const processingRef = useRef(false);
  const autoSubmitTimeoutRef = useRef(null);
  const burstRef = useRef({ count: 0, lastAt: 0 });
  const scannableItems = useMemo(
    () => inventoryItems.filter(item => item.asset && item.asset.type !== "Kit"),
    [inventoryItems]
  );
  const pickedItemIds = useMemo(() => {
    const ids = new Set(scannableItems.filter(item => item.pickedStatus).map(item => item.id));
    pickedItems.forEach(item => ids.add(item.id));
    return ids;
  }, [scannableItems, pickedItems]);
  const totalRemaining = useMemo(
    () => scannableItems.filter(item => !pickedItemIds.has(item.id)).length,
    [scannableItems, pickedItemIds]
  );
  const allPickedItems = useMemo(
    () => {
      const itemsById = new Map(
        scannableItems
          .filter(item => item.pickedStatus)
          .map(item => [
            item.id,
            {
              id: item.id,
              itemId: item.asset?.itemId,
              description: item.asset?.description,
              serialNumber: item.asset?.serialNumber,
              image: (item.asset?.images || []).find(img => img.default)?.url || item.asset?.images?.[0]?.url,
            },
          ])
      );

      pickedItems.forEach(item => itemsById.set(item.id, item));

      return Array.from(itemsById.values());
    },
    [scannableItems, pickedItems]
  );

  useEffect(() => () => {
    if (autoSubmitTimeoutRef.current) {
      window.clearTimeout(autoSubmitTimeoutRef.current);
    }
  }, []);

  const markRequestPickedIfComplete = async pickedIds => {
    const allItemsPicked = scannableItems.every(item => pickedIds.has(item.id));
    if (!allItemsPicked) return;

    await updateInventoryRequest({
      variables: {
        id: inventoryRequest.id,
        input: { vaStatus: { picked: true } },
      },
    });
  };

  const pickItem = async (matchedItem, nextPickedIds = pickedItemIds) => {
    await updateInventoryItem({
      variables: {
        id: matchedItem.id,
        assetId: matchedItem.assetId,
        quantity: matchedItem.quantity,
        pickedStatus: true,
      },
    });
    await markRequestPickedIfComplete(nextPickedIds);
  };

  const clearInput = () => {
    cancelPendingAutoSubmit();
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  const cancelPendingAutoSubmit = () => {
    if (autoSubmitTimeoutRef.current) {
      window.clearTimeout(autoSubmitTimeoutRef.current);
      autoSubmitTimeoutRef.current = null;
    }
  };

  const resetBurstTracking = () => {
    burstRef.current = { count: 0, lastAt: 0 };
    cancelPendingAutoSubmit();
  };

  const focusSearchInput = () => {
    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };

  const scheduleAutoSubmit = () => {
    cancelPendingAutoSubmit();
    autoSubmitTimeoutRef.current = window.setTimeout(() => {
      const nextValue = formikRef.current?.values?.code?.trim();
      if (!nextValue || nextValue.length < AUTO_SUBMIT_MIN_LENGTH || processingRef.current) return;
      formikRef.current?.submitForm();
    }, AUTO_SUBMIT_IDLE_MS);
  };

  const handleSearchKeyDown = event => {
    if (event.key === "Enter") {
      cancelPendingAutoSubmit();
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (event.key === "Backspace" || event.key === "Delete") {
      resetBurstTracking();
      return;
    }

    if (event.key.length !== 1) return;

    const now = Date.now();
    const nextCount = now - burstRef.current.lastAt <= BURST_KEY_GAP_MS ? burstRef.current.count + 1 : 1;
    burstRef.current = { count: nextCount, lastAt: now };

    const nextLength = (event.currentTarget.value?.length || 0) + 1;
    if (nextCount >= AUTO_SUBMIT_MIN_LENGTH && nextLength >= AUTO_SUBMIT_MIN_LENGTH) {
      scheduleAutoSubmit();
    }
  };

  const handleSearchPaste = event => {
    const pastedValue = event.clipboardData?.getData("text")?.trim() || "";
    if (pastedValue.length < AUTO_SUBMIT_MIN_LENGTH) return;

    burstRef.current = { count: pastedValue.length, lastAt: Date.now() };
    scheduleAutoSubmit();
  };

  const addPickedItem = item => {
    setPickedItems(prev => [
      ...prev,
      {
        id: item.id,
        itemId: item.asset?.itemId,
        description: item.asset?.description,
        serialNumber: item.asset?.serialNumber,
        image: (item.asset?.images || []).find(img => img.default)?.url || item.asset?.images?.[0]?.url,
      },
    ]);
  };

  const processScanValue = async scannedValue => {
    if (processingRef.current) return;

    const trimmedValue = scannedValue.trim();
    if (!trimmedValue) {
      notifyError("Please enter a value");
      return;
    }

    processingRef.current = true;
    cancelPendingAutoSubmit();
    setLoading(true);

    let matchedItem = null;
    let matchedField = null;

    for (const item of scannableItems) {
      if (pickedItemIds.has(item.id) || !item.asset) continue;
      const scanFields = getScanFields(item.asset);
      const scanField = scanFields.find(([value]) => value === trimmedValue);
      if (scanField) {
        matchedItem = item;
        matchedField = scanField;
        break;
      }
    }

    if (matchedItem) {
      const [value, label] = matchedField;
      const nextPickedIds = new Set(pickedItemIds);
      nextPickedIds.add(matchedItem.id);
      try {
        await pickItem(matchedItem, nextPickedIds);
        addPickedItem(matchedItem);
        notifySuccess(`Successfully picked ${label}: ${value}`);
        await onScanComplete();
        clearInput();
        focusSearchInput();
        if (nextPickedIds.size >= scannableItems.length) {
          onClose();
        }
      } catch {
        notifyError("Failed to update item status");
      } finally {
        processingRef.current = false;
        setLoading(false);
      }
      return;
    }

    const unpickedItems = scannableItems.filter(item => !pickedItemIds.has(item.id));
    const mobileMatches = findItemsByMobileLocation(unpickedItems, trimmedValue);

    if (mobileMatches.length > 0) {
      setMobileLocationItems(mobileMatches);
      processingRef.current = false;
      setLoading(false);
      return;
    }

    const alreadyPickedItem = inventoryItems.find(item => {
      if (item.asset) {
        const scanFields = getScanFields(item.asset);
        return scanFields.some(([value]) => value === trimmedValue) && pickedItemIds.has(item.id);
      }
      return false;
    });

    if (alreadyPickedItem) {
      notifyError("This item has already been picked");
    } else {
      notifyError("This item was not found on this outbound order. Please confirm you've pulled the correct item");
    }

    processingRef.current = false;
    setLoading(false);
  };

  const handleConfirmMobileLocation = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    setLoading(true);
    try {
      const nextPickedIds = new Set(pickedItemIds);
      mobileLocationItems.forEach(item => nextPickedIds.add(item.id));
      for (const item of mobileLocationItems) {
        await pickItem(item, nextPickedIds);
      }
      setPickedItems(prev => [
        ...prev,
        ...mobileLocationItems.map(item => ({
          id: item.id,
          itemId: item.asset?.itemId,
          description: item.asset?.description,
          serialNumber: item.asset?.serialNumber,
          image: (item.asset?.images || []).find(img => img.default)?.url || item.asset?.images?.[0]?.url,
        })),
      ]);
      notifySuccess(`Successfully picked ${mobileLocationItems.length} item(s)`);
      await onScanComplete();
      setMobileLocationItems(null);
      clearInput();
      focusSearchInput();
      if (nextPickedIds.size >= scannableItems.length) {
        onClose();
      }
    } catch {
      notifyError("Failed to update item status");
    } finally {
      processingRef.current = false;
      setLoading(false);
    }
  };

  const handleManualSubmit = async values => {
    await processScanValue(values.code || "");
  };

  const handleDetected = async code => {
    await processScanValue(code || "");
  };

  if (mobileLocationItems) {
    return (
      <Dialog open onClose={() => setMobileLocationItems(null)} maxWidth={false} sx={{ '& .MuiDialog-paper': { width: 400 } }}>
        <DialogTitle>
          <IconButton
            onClick={() => setMobileLocationItems(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography align="center" fontWeight={500} fontSize={22} color="text.primary">
            Confirm Items
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={1} align="center">
            {mobileLocationItems.length} item(s) found at this mobile location:
          </Typography>
          <List dense disablePadding>
            {mobileLocationItems.map((item, i) => {
              const images = item.asset?.images || [];
              const imageUrl = (images.find(img => img.default) || images[0])?.url;
              return (
                <React.Fragment key={item.id}>
                  {i > 0 && <Divider />}
                  <ListItem disableGutters sx={{ py: 1 }}>
                    <Box mr={1.5} flexShrink={0}>
                      <AssetImage width={60} height={50} image={imageUrl} />
                    </Box>
                    <ListItemText
                      primary={item.asset?.itemId}
                      secondary={item.asset?.description}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
          {loading && <LinearProgress sx={{ mt: 1 }} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setMobileLocationItems(null)} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmMobileLocation} disabled={loading}>
            Confirm All
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative", px: 6, pt: 3 }}>
        <Typography variant="h5" color="text.primary" fontWeight={500} textAlign="center">
          Scan Items
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mt={0.5} fontWeight={500}>
          {Math.max(totalRemaining, 0)} more remaining
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 220, maxHeight: "70vh", overflow: "auto" }}>
        <Box pt={2} />
        <Formik innerRef={formikRef} initialValues={{ code: "" }} onSubmit={handleManualSubmit}>
          {({ values }) => (
            <Form>
              <ScannerInput
                autoFocus
                size="large"
                name="code"
                label="Search"
                inputRef={searchInputRef}
                showNAButton={false}
                disabled={loading}
                onDetected={handleDetected}
                onKeyDown={handleSearchKeyDown}
                onPaste={handleSearchPaste}
                scanContainerProps={{ justifyContent: "flex-start" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          alignSelf: "stretch",
                          borderLeft: theme => `1px solid ${theme.palette.divider}`,
                          mr: 1,
                        }}
                      />
                      <IconButton type="submit" disabled={loading || !values.code.trim()}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Form>
          )}
        </Formik>

        {loading && <LinearProgress sx={{ mt: 2 }} />}

        {allPickedItems.length > 0 && (
          <Box mt={3}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Items Picked So Far ({allPickedItems.length})
              </Typography>
            </Box>
            <List
              sx={{
                bgcolor: "success.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                py: 0,
              }}
            >
              {allPickedItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem sx={{ py: 1, px: 2 }}>
                    <ListItemAvatar sx={{ minWidth: 56 }}>
                      <AssetImage image={item.image} width={44} height={44} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {item.itemId}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                          {item.serialNumber && (
                            <Typography variant="body2" display="block" color="text.secondary" mt={0.25}>
                              S/N: {item.serialNumber}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
